/* eslint-disable import/no-unresolved */
import { useCallback, useMemo, useRef, useState } from 'react';

import { DollarCircleOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Tooltip } from 'antd';
import {
  ProductBuy,
  ProductBuyHandle,
} from 'components/BusinessLogic/Product/ProductBuy';
import TrolleyLimitModal from 'components/Modals/TrolleyLimitModal';
import Monetary from 'components/UI/Data/Monetary';
import { BREAKPOINTS_NUM } from 'constants/Global/Breakpoints.const';
import { INVALID_QUANTITY_MESSAGE } from 'constants/Global/QuantityValidation.const';
import { PackingTypeSale, PackingTypeSaleMap } from 'enums/packingTypeSale';
import { usePurchaseInfoDisplayGfp } from 'hooks/usePurchaseInfoDisplay';
import useWindowSize from 'hooks/window-size';
import ProductOffer from 'model/ProductOffer';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import gfpOfferStore from 'store/state/gfpOffer/gfpOfferStore';
import offerDetailsStore from 'store/state/offerDetails/offerDetailsStore';
import { sessionActions } from 'store/state/slices';
import { IGfpOffer } from 'types/api/GfpOffer';
import { formatDecimal } from 'utils/number';
import { calculateMinBySaleType } from 'utils/util';
import { validatorUtils } from 'utils/validator';
import { useShallow } from 'zustand/react/shallow';
import * as S from './TypeSaleInput.styled';

const ModalActions = {
  cart: 'Cart',
  buy: 'Buy',
};

type IProps = {
  gfpOffer: IGfpOffer;
  productOffer: ProductOffer;
  priceOnly?: boolean;
};

export default function TypeSaleInput(props: IProps) {
  const { gfpOffer, productOffer, priceOnly } = props;
  const [width] = useWindowSize();

  const { packingSelected } = gfpOfferStore(
    useShallow((state) => ({
      packingSelected: state.packingSelected,
    })),
  );

  const {
    hasShippingFee,
    billingDateValue,
    deliveryDateValue,
    setDisplayModalInformation,
    setHandleOnConfirmationDeliveryDate,
    setIsLoading,
  } = offerDetailsStore(
    useShallow((state) => ({
      hasShippingFee: state.hasShippingFee,
      billingDateValue: state.billingDateValue,
      deliveryDateValue: state.deliveryDateValue,
      setDisplayModalInformation: state.setDisplayModalInformation,
      setHandleOnConfirmationDeliveryDate:
        state.setHandleOnConfirmationDeliveryDate,
      setIsLoading: state.setIsLoading,
    })),
  );

  const { handleSearch } = gfpOfferStore(
    useShallow((state) => ({
      handleSearch: state.handleSearch,
    })),
  );

  const [fastBuy, setFastBuy] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const productBuyRef = useRef<ProductBuyHandle>(null);

  const handleSubmit = (formValues: any) => {
    formValues.packingSelectedId = packingSelected.id;
    productBuyRef.current?.verifyXpedAndBuyFunc(formValues);
  };

  const isPackingDisabled = useMemo(
    () =>
      !productOffer?.packagingPrice || !gfpOffer?.maxAvailablePackingsQuantity,
    [productOffer?.packagingPrice, gfpOffer?.maxAvailablePackingsQuantity],
  );
  const isLayerDisabled = useMemo(
    () => !productOffer?.layerPrice || !gfpOffer?.maxAvailableLayerQuantity,
    [productOffer?.layerPrice, gfpOffer?.maxAvailableLayerQuantity],
  );
  const isTrolleyDisabled = useMemo(
    () => !productOffer?.trolleyPrice || !gfpOffer?.maxAvailableTrolleyQuantity,
    [productOffer?.trolleyPrice, gfpOffer?.maxAvailableTrolleyQuantity],
  );

  const initialPackingType = useMemo(() => {
    if (!isPackingDisabled) {
      return PackingTypeSale.Packing;
    }

    if (!isLayerDisabled) {
      return PackingTypeSale.Layer;
    }

    return PackingTypeSale.Trolley;
  }, [isPackingDisabled, isLayerDisabled]);

  const [packingTypeSelected, setPackingTypeSelected] =
    useState<PackingTypeSale>(initialPackingType);

  const handleTypeSale = (packingType: PackingTypeSale) => {
    setPackingTypeSelected(packingType);
    setTextInputTypeSale(`${PackingTypeSaleMap[packingType]}(s)`);
  };

  const submitPurchase = useCallback(() => {
    setFastBuy(true);
    form?.submit?.();
  }, [form]);

  const handleClickFastBuy = useCallback(() => {
    if (purchaseInfo.exceedsTrolley) {
      setPendingGfpAction('fastBuy');
      setShowTrolleyModal(true);
      return;
    }
    if (width >= BREAKPOINTS_NUM.tablet) {
      submitPurchase();
      return;
    }

    setModalAction(ModalActions.buy);
    setModalOpen(true);
  }, [submitPurchase, width]);

  const handlePurchaseComplete = useCallback(() => {
    handleSearch(
      gfpOffer.lkpOfferId,
      productOffer.offerType,
      String(packingSelected.id),
      moment.utc(productOffer?.lkpAuctionDate).format('YYYY-MM-DD'),
    );
    setFastBuy(false);
  }, [
    gfpOffer.lkpOfferId,
    productOffer.offerType,
    handleSearch,
    packingSelected.id,
    productOffer.lkpAuctionDate,
  ]);

  const isPackingChecked = useMemo(
    () => packingTypeSelected === PackingTypeSale.Packing,
    [packingTypeSelected],
  );
  const isLayerChecked = useMemo(
    () => packingTypeSelected === PackingTypeSale.Layer,
    [packingTypeSelected],
  );
  const isTrolleyChecked = useMemo(
    () => packingTypeSelected === PackingTypeSale.Trolley,
    [packingTypeSelected],
  );

  const getMaxAvailable = useMemo(() => {
    if (packingTypeSelected === PackingTypeSale.Packing) {
      return {
        label: gfpOffer?.maxAvailablePackingLabel || '',
        max: gfpOffer?.maxAvailablePackingsQuantity || 0,
        min: gfpOffer?.minAvailablePackingsQuantity,
      };
    }
    if (packingTypeSelected === PackingTypeSale.Layer) {
      return {
        label: gfpOffer?.maxAvailableLayerLabel || '',
        max: gfpOffer?.maxAvailableLayerQuantity || 0,
        min: gfpOffer?.minAvailableLayerQuantity,
      };
    }
    return {
      label: gfpOffer?.maxAvailableTrolleyLabel || '',
      max: gfpOffer?.maxAvailableTrolleyQuantity || 0,
      min: gfpOffer?.minAvailableTrolleyQuantity,
    };
  }, [gfpOffer, packingTypeSelected]);

  const [textInputTypeSale, setTextInputTypeSale] = useState<string>(
    `${PackingTypeSaleMap[PackingTypeSale.Packing]}(s)`,
  );

  const getErrorMinMessage = useCallback(() => {
    if (packingTypeSelected === PackingTypeSale.Packing) {
      return `Quantidade mínima: ${gfpOffer?.minAvailablePackingsQuantity} ${textInputTypeSale}`;
    }
    if (packingTypeSelected === PackingTypeSale.Layer) {
      return `Quantidade mínima: ${gfpOffer?.minAvailableLayerQuantity} ${textInputTypeSale}`;
    }
    return `Quantidade mínima: ${gfpOffer?.minAvailableTrolleyQuantity} ${textInputTypeSale}`;
  }, [
    packingTypeSelected,
    gfpOffer?.minAvailablePackingsQuantity,
    gfpOffer?.minAvailableLayerQuantity,
    gfpOffer?.minAvailableTrolleyQuantity,
    textInputTypeSale,
  ]);

  const getLabel = useCallback(() => {
    if (packingTypeSelected === PackingTypeSale.Packing) {
      return gfpOffer?.maxAvailablePackingLabel || '';
    }
    if (packingTypeSelected === PackingTypeSale.Layer) {
      return gfpOffer?.maxAvailableLayerLabel || '';
    }
    return gfpOffer?.maxAvailableTrolleyLabel || '';
  }, [
    packingTypeSelected,
    gfpOffer?.maxAvailableTrolleyLabel,
    gfpOffer?.maxAvailableLayerLabel,
    gfpOffer?.maxAvailablePackingLabel,
  ]);

  const getMultipleCalc = useCallback(() => {
    if (packingTypeSelected === PackingTypeSale.Packing) {
      return gfpOffer?.minAvailablePackingsQuantity;
    }
    if (packingTypeSelected === PackingTypeSale.Layer) {
      return gfpOffer?.minAvailableLayerQuantity;
    }
    return gfpOffer?.minAvailableTrolleyQuantity;
  }, [
    packingTypeSelected,
    gfpOffer?.minAvailablePackingsQuantity,
    gfpOffer?.minAvailableLayerQuantity,
    gfpOffer?.minAvailableTrolleyQuantity,
  ]);

  const [quantityPacking, setQuantityPacking] = useState(0);
  const isQuantityValid = useMemo(() => quantityPacking > 0, [quantityPacking]);

  const calculatedQuantity = useMemo(() => {
    if (quantityPacking) {
      if (packingTypeSelected === PackingTypeSale.Packing) {
        const calculated = quantityPacking * gfpOffer?.quantityPerPacking;
        return calculated;
      }
      if (packingTypeSelected === PackingTypeSale.Layer) {
        const calculated =
          quantityPacking *
          (gfpOffer?.quantityPerLayer * gfpOffer?.quantityPerPacking);
        return calculated;
      }
      if (packingTypeSelected === PackingTypeSale.Trolley) {
        const calculated =
          quantityPacking *
          (gfpOffer?.quantityPerLayer *
            gfpOffer?.quantityPerTrolley *
            gfpOffer?.quantityPerPacking);
        return calculated;
      }
    }
    return 1;
  }, [gfpOffer, packingTypeSelected, quantityPacking]);

  const currentPrice = useMemo(() => {
    if (!quantityPacking || !productOffer.specialPrice) {
      return productOffer.price;
    }
    return calculatedQuantity >= productOffer.minimumQuantity
      ? productOffer.specialPrice
      : productOffer.price;
  }, [
    calculatedQuantity,
    productOffer.minimumQuantity,
    productOffer.specialPrice,
    productOffer.price,
    quantityPacking,
  ]);

  const unitPrice = useMemo(() => {
    let newValue = 0;
    if (currentPrice > 0 && packingSelected?.additionalCost) {
      newValue = currentPrice + productOffer.packingCost;
    } else {
      newValue = currentPrice;
    }
    return newValue;
  }, [currentPrice, packingSelected?.additionalCost, productOffer.packingCost]);

  const packagingPriceWithCost = useMemo(() => {
    if (productOffer.packagingPrice > 0 && packingSelected?.additionalCost) {
      return productOffer.packagingPrice + productOffer.packingCost;
    }
    return productOffer.packagingPrice;
  }, [productOffer.packagingPrice, productOffer.packingCost, packingSelected?.additionalCost]);

  const layerPriceWithCost = useMemo(() => {
    if (productOffer.layerPrice > 0 && packingSelected?.additionalCost) {
      return productOffer.layerPrice + productOffer.packingCost;
    }
    return productOffer.layerPrice;
  }, [productOffer.layerPrice, productOffer.packingCost, packingSelected?.additionalCost]);

  const trolleyPriceWithCost = useMemo(() => {
    if (productOffer.trolleyPrice > 0 && packingSelected?.additionalCost) {
      return productOffer.trolleyPrice + productOffer.packingCost;
    }
    return productOffer.trolleyPrice;
  }, [productOffer.trolleyPrice, productOffer.packingCost, packingSelected?.additionalCost]);

  const clickCloseModalInformation = useCallback(() => {
    setDisplayModalInformation(false);
  }, [setDisplayModalInformation]);

  const handleOnConfirmationDeliveryDate = useCallback(() => {
    dispatch(
      sessionActions.setDates({
        billingDate:
          productOffer.offerType === 1 || productOffer.offerType === 2
            ? productOffer.lkpAuctionDate.toString()
            : billingDateValue?.set('hour', 0).format('YYYY-MM-DD') ?? '',
        deliveryDate:
          productOffer.offerType === 1 || productOffer.offerType === 2
            ? productOffer.lkpAuctionDate.toString()
            : deliveryDateValue?.set('hour', 0).format('YYYY-MM-DD') ?? '',
      }),
    );
    clickCloseModalInformation();
    form.submit();
  }, [
    billingDateValue,
    deliveryDateValue,
    dispatch,
    form,
    productOffer.lkpAuctionDate,
    productOffer.offerType,
    clickCloseModalInformation,
  ]);

  const onSetDisplayModalInformation = useCallback(
    (display: boolean) => {
      if (display) {
        setHandleOnConfirmationDeliveryDate(handleOnConfirmationDeliveryDate);
      }

      setDisplayModalInformation(display);
    },
    [
      setHandleOnConfirmationDeliveryDate,
      handleOnConfirmationDeliveryDate,
      setDisplayModalInformation,
    ],
  );

  const [modalAction, setModalAction] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [showTrolleyModal, setShowTrolleyModal] = useState(false);
  const [pendingGfpAction, setPendingGfpAction] = useState<'addToCart' | 'fastBuy' | null>(null);

  const gfpShippingFee = useMemo(
    () =>
      hasShippingFee
        ? productOffer.shippingFeeFilials?.[0]?.productShippingValue ?? 0
        : 0,
    [hasShippingFee, productOffer.shippingFeeFilials],
  );

  const purchaseInfo = usePurchaseInfoDisplayGfp(
    packingTypeSelected,
    gfpOffer,
    quantityPacking,
    gfpShippingFee,
  );

  const handleAddToCart = useCallback(() => {
    if (!isQuantityValid) {
      return;
    }
    if (purchaseInfo.exceedsTrolley) {
      setPendingGfpAction('addToCart');
      setShowTrolleyModal(true);
      return;
    }
    if (width >= BREAKPOINTS_NUM.tablet) {
      form?.submit?.();
      setModalOpen(false);
      return;
    }

    setModalAction(ModalActions.cart);
    setModalOpen(true);
  }, [form, width, purchaseInfo.exceedsTrolley, isQuantityValid]);

  const handleModalConfirmButtonClick = useCallback(() => {
    if (!isQuantityValid) {
      return;
    }
    if (purchaseInfo.exceedsTrolley) {
      setModalOpen(false);
      setPendingGfpAction(modalAction === ModalActions.buy ? 'fastBuy' : 'addToCart');
      setShowTrolleyModal(true);
      return;
    }
    if (modalAction === ModalActions.cart) form.submit();
    else if (modalAction === ModalActions.buy) submitPurchase();
  }, [
    form,
    modalAction,
    submitPurchase,
    purchaseInfo.exceedsTrolley,
    isQuantityValid,
  ]);

  const handleTrolleyConfirm = useCallback(() => {
    setShowTrolleyModal(false);
    if (pendingGfpAction === 'fastBuy') {
      submitPurchase();
    } else {
      form?.submit?.();
    }
    setPendingGfpAction(null);
  }, [pendingGfpAction, form, submitPurchase]);

  const BuyScreen = (
    <>
      <S.CheckboxContainer>
        <S.CheckBoxButton
          onClick={() => {
            if (!isPackingDisabled) {
              handleTypeSale(PackingTypeSale.Packing);
            }
          }}
          type="button"
          style={{
            opacity: isPackingDisabled ? 0.5 : 1,
            cursor: isPackingDisabled ? 'not-allowed' : 'pointer',
          }}
          {...props}
        >
          <S.Radio
            value={PackingTypeSale.Packing}
            checked={isPackingChecked}
            disabled={isPackingDisabled}
          />
          <S.SaleTypePriceContainer>
            {PackingTypeSaleMap[PackingTypeSale.Packing]}
            <S.SaleTypePrice checked={isPackingChecked}>
              R$ {formatDecimal(packagingPriceWithCost)}
            </S.SaleTypePrice>
          </S.SaleTypePriceContainer>
        </S.CheckBoxButton>
        <S.SaleTypeDivider />
        <S.CheckBoxButton
          onClick={() => {
            if (!isLayerDisabled) {
              handleTypeSale(PackingTypeSale.Layer);
            }
          }}
          type="button"
          style={{
            opacity: isLayerDisabled ? 0.5 : 1,
            cursor: isLayerDisabled ? 'not-allowed' : 'pointer',
          }}
          {...props}
        >
          <S.Radio
            value={PackingTypeSale.Layer}
            checked={isLayerChecked}
            disabled={isLayerDisabled}
          />
          <S.SaleTypePriceContainer>
            {PackingTypeSaleMap[PackingTypeSale.Layer]}
            <S.SaleTypePrice checked={isLayerChecked}>
              R$ {formatDecimal(layerPriceWithCost)}
            </S.SaleTypePrice>
          </S.SaleTypePriceContainer>
        </S.CheckBoxButton>
        <S.SaleTypeDivider />
        <S.CheckBoxButton
          onClick={() => {
            if (!isTrolleyDisabled) {
              handleTypeSale(PackingTypeSale.Trolley);
            }
          }}
          type="button"
          style={{
            opacity: isTrolleyDisabled ? 0.5 : 1,
            cursor: isTrolleyDisabled ? 'not-allowed' : 'pointer',
          }}
          {...props}
        >
          <S.Radio
            value={PackingTypeSale.Trolley}
            checked={isTrolleyChecked}
            disabled={isTrolleyDisabled}
          />
          <S.SaleTypePriceContainer>
            {PackingTypeSaleMap[PackingTypeSale.Trolley]}
            <S.SaleTypePrice checked={isTrolleyChecked}>
              R$ {formatDecimal(trolleyPriceWithCost)}
            </S.SaleTypePrice>
          </S.SaleTypePriceContainer>
        </S.CheckBoxButton>
      </S.CheckboxContainer>
      <S.PriceInputContainer>
        <Form form={form} onFinish={handleSubmit}>
          <Form.Item
            name="quantityPacking"
            rules={[
              {
                required: true,
                message: 'A quantidade é obrigatória.',
              },
              {
                validator: (_, value) =>
                  !value || Number(value) <= getMaxAvailable.max
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          `Quantidade máxima: ${getMaxAvailable.max} ${textInputTypeSale}`,
                        ),
                      ),
              },
              {
                validator: (_, value) =>
                  (value || Number(value)) >=
                  calculateMinBySaleType(packingTypeSelected, packingSelected)
                    ? Promise.resolve()
                    : Promise.reject(new Error(getErrorMinMessage())),
              },
              {
                validator: (_, value) => {
                  const multipleValue = getMultipleCalc() || 1;
                  const maxAvailableLabel = getLabel();

                  return validatorUtils.validMultipleWithNamedParams({
                    value,
                    multiple: packingSelected?.multiple,
                    packingType: packingTypeSelected,
                    maxAvailable: maxAvailableLabel,
                    valueTypeSale: multipleValue,
                    label: textInputTypeSale,
                  });
                },
              },
            ]}
          >
            <Input
              autoComplete="off"
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              placeholder="Digite aqui"
              className="input-quantity"
              addonAfter={textInputTypeSale}
              onChange={({ target }) => {
                const value = Number(target.value);
                if (value >= 0) {
                  setQuantityPacking(value);
                  form.setFieldsValue({ quantityPacking: value });
                }
              }}
            />
            <S.QuantityLabelContainer>
              <S.InfoFirstRow>
                <S.MinQuantityProduct>{purchaseInfo.qtdMinimaText}</S.MinQuantityProduct>
                <span>Total disponível: {getMaxAvailable.max}</span>
                <S.ShippingFee>{purchaseInfo.shippingFeeText}</S.ShippingFee>
              </S.InfoFirstRow>
              {purchaseInfo.compositionLine && (
                <S.CompositionLine>{purchaseInfo.compositionLine}</S.CompositionLine>
              )}
              {purchaseInfo.totalPedidoText && (
                <S.TotalPedidoLine>{purchaseInfo.totalPedidoText}</S.TotalPedidoLine>
              )}
            </S.QuantityLabelContainer>
          </Form.Item>
          <ProductBuy
            ref={productBuyRef}
            billingDateValue={billingDateValue}
            deliveryDateValue={deliveryDateValue}
            fastBuy={fastBuy}
            productOffer={productOffer}
            selectedTypeSale={packingTypeSelected}
            onFinish={handlePurchaseComplete}
            onResetFields={form.resetFields}
            onSetDisplayModalInformation={onSetDisplayModalInformation}
            setLoading={setIsLoading}
          />
        </Form>
      </S.PriceInputContainer>
    </>
  );

  return (
    <S.TypeSaleInput>
      <S.TypeSaleContainer>
        <S.SaleInputContainer>
          <S.PriceContainer>
            <S.PriceLabel>Preço unitário:</S.PriceLabel>
            <S.PriceValue>
              <Monetary symbol value={unitPrice} />
            </S.PriceValue>
          </S.PriceContainer>
          {!priceOnly && BuyScreen}
        </S.SaleInputContainer>
      </S.TypeSaleContainer>
      <S.ButtonsContainer>
        <Tooltip title={isQuantityValid ? '' : INVALID_QUANTITY_MESSAGE}>
          <S.ButtonTooltipWrapper isDisabled={!isQuantityValid}>
            <S.BuyButton
              type="primary"
              onClick={handleClickFastBuy}
              disabled={!isQuantityValid}
            >
              <S.ButtonText>Comprar agora</S.ButtonText>
              <DollarCircleOutlined
                style={{ fontSize: '16px', height: '16px' }}
              />
            </S.BuyButton>
          </S.ButtonTooltipWrapper>
        </Tooltip>
        <Tooltip title={isQuantityValid ? '' : INVALID_QUANTITY_MESSAGE}>
          <S.ButtonTooltipWrapper isDisabled={!isQuantityValid}>
            <S.BuyButton
              className="cart-button"
              onClick={handleAddToCart}
              color="#f78e1e"
              disabled={!isQuantityValid}
            >
              <S.ButtonText>Adicionar ao carrinho</S.ButtonText>
              <ShoppingCartOutlined
                style={{ fontSize: '16px', height: '16px' }}
              />
            </S.BuyButton>
          </S.ButtonTooltipWrapper>
        </Tooltip>
      </S.ButtonsContainer>
      <Modal
        closable
        visible={modalOpen}
        width={width >= BREAKPOINTS_NUM.tabletMini ? '480px' : '300px'}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button
            key={`${gfpOffer.gfpNumber}-1`}
            onClick={() => setModalOpen(false)}
          >
            Cancelar
          </Button>,
          <Tooltip
            key={`${gfpOffer.gfpNumber}-2`}
            title={isQuantityValid ? '' : INVALID_QUANTITY_MESSAGE}
          >
            <Button
              type="primary"
              onClick={handleModalConfirmButtonClick}
              disabled={!isQuantityValid}
            >
              {modalAction === ModalActions.buy
                ? 'Confirmar compra'
                : 'Adicionar ao carrinho'}
            </Button>
          </Tooltip>,
        ]}
        bodyStyle={{ padding: '16px' }}
      >
        <S.ModalHeader>
          <DollarCircleOutlined className="icon" />
          {modalAction === ModalActions.buy
            ? 'Comprar agora:'
            : 'Adicionar ao carrinho:'}{' '}
          {productOffer.longName}
          {productOffer.conceptName && (
            <S.ConceptName>{productOffer.conceptName}</S.ConceptName>
          )}
        </S.ModalHeader>
        <S.CtaTitle>
          Selecione a modalidade de compra e a quantidade:
        </S.CtaTitle>
        {BuyScreen}
      </Modal>

      <TrolleyLimitModal
        visible={showTrolleyModal}
        totalUnits={purchaseInfo.totalUnits}
        quantityOrdered={quantityPacking}
        selectedTypeSale={packingTypeSelected}
        unitsPerType={purchaseInfo.unitsPerType}
        totalPrice={purchaseInfo.totalUnits * unitPrice}
        shippingFeeTotal={
          hasShippingFee ? purchaseInfo.totalUnits * gfpShippingFee : undefined
        }
        onConfirm={handleTrolleyConfirm}
        onCancel={() => { setShowTrolleyModal(false); setPendingGfpAction(null); }}
      />
    </S.TypeSaleInput>
  );
}
